import { ActionIcon, Menu, Skeleton } from "@mantine/core";
import { IconChevronDown, IconLayoutDashboard } from "@tabler/icons-react";
import { Link, Outlet, generatePath, useParams } from "react-router";
import { useProject } from "../../../hooks/useProject";
import { useProjects } from "../../../hooks/useProjects";
import { ROUTES } from "../../../routes";
import type { ProjectMinimal } from "../../../utils/backend-types";
import { SeparatorIcon } from "../icons/SeparatorIcon";
import { MenuItem } from "../MenuItem/MenuItem";
import { MenuLink } from "../MenuLink/MenuLink";

interface ProjectListBreadcrumbProps {
  listPath: string;
  projectPath: string;
}

function ProjectListBreadcrumb({
  listPath,
  projectPath
}: ProjectListBreadcrumbProps) {
  const { projectId } = useParams();
  const { data: projects, isLoading: projectsLoading } = useProjects({
    options: {
      refetchInterval: false,
      refetchOnWindowFocus: false
    }
  });
  const { project } = useProject(projectId);
  const projectUrl = generatePath(projectPath, { projectId });

  return (
    <>
      <MenuItem>
        <Link
          style={{
            display: "flex",
            alignItems: "center"
          }}
          to={listPath}
        >
          <IconLayoutDashboard
            aria-label="Dashboard"
            color="var(--mantine-color-neutral-7)"
          />
        </Link>
      </MenuItem>
      <SeparatorIcon />
      {projectsLoading && <Skeleton h="lg" mr="md" w={160} />}
      {project && projects && (
        <>
          <MenuItem flex="0 999 auto" miw="6rem">
            <MenuLink text={project.name} to={projectUrl} />
          </MenuItem>
          {projects.length > 1 && (
            <MenuItem>
              <ProjectSubMenu
                currentProjectId={project.id}
                projects={projects}
              />
            </MenuItem>
          )}
        </>
      )}
      <SeparatorIcon />
      <Outlet />
    </>
  );
}

interface ProjectSubMenuProps {
  projects: Array<ProjectMinimal>;
  currentProjectId: string;
}

function ProjectSubMenu({ projects, currentProjectId }: ProjectSubMenuProps) {
  const items = projects
    .filter((project) => project.id !== currentProjectId)
    .map((project) => {
      const path = generatePath(ROUTES.project.sites, {
        projectId: project.id
      });
      return (
        <Menu.Item component={Link} key={project.id} to={path}>
          {project.name}
        </Menu.Item>
      );
    });

  return (
    <Menu aria-label="Projektauswahl">
      <Menu.Target>
        <ActionIcon c="neutral.8" mt={4} size="xs" variant="white">
          <IconChevronDown strokeWidth={1} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown
        mah="calc(100vh - 100px)"
        maw={800}
        style={{ overflowY: "auto" }}
      >
        {items}
      </Menu.Dropdown>
    </Menu>
  );
}

export { ProjectListBreadcrumb };
